import { useCallback, useEffect, useRef } from 'react';
import type { PricingModelType } from '@elfsight/configurator-contracts';
import { binarySearchFontSize } from './binary-search-font-size';
import { useMeasurer } from './use-measurer';

interface ResponsivePriceSizeProps {
  initialValueFont: number;
  initialAffixFont: number;
  minFontSize: number;
  isVertical: boolean;
  pricingModel: PricingModelType;
}

export function useResponsivePriceSize({
  initialValueFont,
  initialAffixFont,
  minFontSize,
  isVertical,
  pricingModel
}: ResponsivePriceSizeProps) {
  const previousPricingModel = useRef<PricingModelType>(pricingModel);
  const previousValueFont = useRef<number>(initialValueFont);
  const previousAffixFont = useRef<number>(initialAffixFont);

  const valueRef = useRef<HTMLDivElement>(null);
  const affixRef = useRef<HTMLDivElement>(null);

  const resizeObserver = useRef<ResizeObserver | null>(null);
  const { measurerRef, parentRef: containerRef } = useMeasurer();

  const handleResize = useCallback(() => {
    const containerWidth = containerRef.current?.offsetWidth;
    const valueWidth = valueRef.current?.offsetWidth ?? 0;

    // Reset font sizes if pricing model changed or font sizes changed
    const shouldReset =
      !containerWidth ||
      !valueWidth ||
      previousPricingModel.current !== pricingModel ||
      previousValueFont.current !== initialValueFont ||
      previousAffixFont.current !== initialAffixFont;

    if (shouldReset) {
      valueRef.current?.style.setProperty('font-size', `${initialValueFont}px`);
      affixRef.current?.style.setProperty('font-size', `${initialAffixFont}px`);
      previousPricingModel.current = pricingModel;
      previousValueFont.current = initialValueFont;
      previousAffixFont.current = initialAffixFont;

      return;
    }

    if (!valueRef.current || !measurerRef.current) {
      return;
    }

    // Есть только значение
    if (!affixRef.current) {
      binarySearchFontSize({
        containerWidth,
        minFontSize: minFontSize,
        maxFontSize: initialValueFont,
        firstElement: valueRef.current,
        measurer: measurerRef.current
      });

      return;
    }

    // Вертикальный леайут
    if (isVertical) {
      binarySearchFontSize({
        containerWidth,
        minFontSize: minFontSize,
        maxFontSize: initialValueFont,
        firstElement: valueRef.current,
        measurer: measurerRef.current
      });

      binarySearchFontSize({
        containerWidth,
        minFontSize: minFontSize,
        maxFontSize: initialAffixFont,
        firstElement: affixRef.current,
        measurer: measurerRef.current
      });

      return;
    }

    // Размер значения и аффикса совпадают
    if (initialAffixFont === initialValueFont) {
      binarySearchFontSize({
        containerWidth,
        minFontSize: minFontSize,
        maxFontSize: initialValueFont,
        firstElement: valueRef.current,
        secondElement: affixRef.current,
        measurer: measurerRef.current,
        shouldResizeSecondElement: true
      });

      return;
    }

    // Общий случай - размеры значения и аффикса разные
    const [smallerFont, largerFont, smallerElement, largerElement] =
      initialValueFont > initialAffixFont
        ? [
            initialAffixFont,
            initialValueFont,
            affixRef.current,
            valueRef.current
          ]
        : [
            initialValueFont,
            initialAffixFont,
            valueRef.current,
            affixRef.current
          ];

    // Уменьшаем размер наибольшего элемента пока он достигнет размера наименьшего
    binarySearchFontSize({
      containerWidth,
      minFontSize: smallerFont,
      maxFontSize: largerFont,
      firstElement: largerElement,
      secondElement: smallerElement,
      secondElementFixedSize: smallerFont,
      measurer: measurerRef.current
    });

    // Если наибольший элемент достиг размеров наименьшего, уменьшаем оба элемента вместе
    // Проверяем актуальный размер после первого binarySearchFontSize
    const currentLargerFontSize =
      parseFloat(largerElement.style.fontSize) || largerFont;
    const reachedSmallerFont = currentLargerFontSize <= smallerFont;
    binarySearchFontSize({
      containerWidth,
      minFontSize: minFontSize,
      maxFontSize: smallerFont,
      firstElement: smallerElement,
      secondElement: largerElement,
      shouldResizeSecondElement: reachedSmallerFont,
      secondElementFixedSize: smallerFont,
      measurer: measurerRef.current
    });
  }, [
    pricingModel,
    isVertical,
    initialAffixFont,
    initialValueFont,
    minFontSize,
    containerRef,
    measurerRef
  ]);

  useEffect(() => {
    if (!containerRef.current || !valueRef.current) {
      return;
    }

    resizeObserver.current = new ResizeObserver(handleResize);
    resizeObserver.current.observe(containerRef.current);
    resizeObserver.current.observe(valueRef.current);

    if (affixRef.current) {
      resizeObserver.current.observe(affixRef.current);
    }

    handleResize();
    document.fonts.addEventListener('loadingdone', handleResize);

    return () => {
      resizeObserver.current?.disconnect();
      document.fonts.removeEventListener('loadingdone', handleResize);
    };
  }, [handleResize]);

  return {
    containerRef,
    valueRef,
    affixRef
  };
}
